import { onCLS, onFCP, onINP, onLCP, onTTFB, Metric } from 'web-vitals';
import axios from 'axios';

/**
 * Performance metric interface matching web-vitals format
 */
interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  navigationType: string;
  timestamp: number;
  url: string;
  userAgent: string;
}

/**
 * Performance monitoring configuration
 */
interface PerformanceMonitorConfig {
  endpoint?: string;
  batchSize?: number;
  flushInterval?: number;
}

/**
 * Production Core Web Vitals monitoring service
 * 
 * Captures real-time performance metrics and sends them to backend
 * for aggregation and dashboard visualization.
 */
class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private endpoint: string;
  private batchSize: number;
  private flushInterval: number;
  private sessionId: string;
  private flushTimer: NodeJS.Timeout | null = null;

  constructor(config: PerformanceMonitorConfig = {}) {
    this.endpoint = config.endpoint || '/api/analytics/web-vitals';
    this.batchSize = config.batchSize || 10;
    this.flushInterval = config.flushInterval || 30000; // 30 seconds
    this.sessionId = this.generateSessionId();
  }

  /**
   * Generate unique session ID for tracking
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Calculate rating based on Core Web Vitals thresholds
   */
  private calculateRating(name: string, value: number): 'good' | 'needs-improvement' | 'poor' {
    const thresholds: Record<string, { good: number; poor: number }> = {
      CLS: { good: 0.1, poor: 0.25 },
      LCP: { good: 2500, poor: 4000 },
      INP: { good: 200, poor: 500 },
      FCP: { good: 1800, poor: 3000 },
      TTFB: { good: 800, poor: 1800 },
    };

    const threshold = thresholds[name];
    if (!threshold) {
      return 'good'; // Default to good for unknown metrics
    }

    if (value <= threshold.good) {
      return 'good';
    } else if (value <= threshold.poor) {
      return 'needs-improvement';
    } else {
      return 'poor';
    }
  }

  /**
   * Capture a performance metric from web-vitals
   */
  captureMetric(metric: Metric): void {
    const performanceMetric: PerformanceMetric = {
      name: metric.name,
      value: metric.value,
      rating: this.calculateRating(metric.name, metric.value),
      delta: metric.delta,
      id: metric.id,
      navigationType: metric.navigationType,
      timestamp: Date.now(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    this.metrics.push(performanceMetric);

    // Flush if batch size reached
    if (this.metrics.length >= this.batchSize) {
      this.flush();
    }
  }

  /**
   * Flush metrics to backend
   */
  async flush(): Promise<void> {
    if (this.metrics.length === 0) {
      return;
    }

    const metricsToSend = [...this.metrics];
    this.metrics = [];

    try {
      await axios.post(this.endpoint, {
        sessionId: this.sessionId,
        timestamp: Date.now(),
        metrics: metricsToSend,
      });
    } catch (error) {
      // Silently handle errors - don't throw to avoid breaking app
      console.error('Failed to send performance metrics:', error);
      // Re-add metrics to queue for retry (with limit to prevent memory issues)
      if (this.metrics.length < 100) {
        this.metrics.unshift(...metricsToSend);
      }
    }
  }

  /**
   * Start monitoring Core Web Vitals
   */
  start(): void {
    // Capture all Core Web Vitals
    onCLS(this.captureMetric.bind(this));
    onFCP(this.captureMetric.bind(this));
    onINP(this.captureMetric.bind(this));
    onLCP(this.captureMetric.bind(this));
    onTTFB(this.captureMetric.bind(this));

    // Set up periodic flush
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.flushInterval);

    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  /**
   * Stop monitoring and cleanup
   */
  stop(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
    this.flush();
  }
}

// Export singleton instance
export const performanceMonitor = new PerformanceMonitor();
